import { View } from 'react-native';
import { Redirect, Stack } from 'expo-router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CustomTextStyles } from '@/components/CustomText';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from "@/utilities/AppUtils";
import * as AppCore from "@/utilities/AppCore";
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Button, Hourglass } from 'react95-native-rabbl';
import { CustomText } from '@/components/CustomText';
import { UNKNOWN_COMMUNITY_MEMBERSHIP } from "@/types/community_membership";
import { SafeAreaView } from 'react-native-safe-area-context';
import { AppText } from '@/components/AppText';
import { AgeModalWeb } from '@/components/AgeModalWeb';
import { AgeModalNative } from '@/components/AgeModalNative';
import * as AgeRange from 'expo-age-range';
import { CustomKeyboardAvoidingView } from '@/components/CustomKeyboardAvoidingView';
import Drawer from '@/components/Drawer';
import { setAgePassed } from '@/state/modals';
import { AppContext } from '@/components/AppContext';
import Cookies from 'js-cookie';

export default function LayoutRouting() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const isAgePassed = useSelector((state: RootState) => state.modals.isAgePassed);
  const [nativeAgeFailed, setNativeAgeFailed] = useState(false);
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  //const { openModal } = useModal();

  const loadInitialData = useCallback(async () => {
    try {
      AppUtilities.info("LayoutRouting loadInitialData isMainLogicalHost = " + isMainLogicalHost + ", domain: " + domain);
      setLoading(true);
      showSpinner();
      setError('');

      let response: any = undefined;
      // If the user isn't signed in, then we want to send a GET request so that it can be cached
      // in the browser and CDN.
      //
      // If the user logs in on one domain and then navigates to another domain
      // all we have is the cookies and that will be used on the initialdata request
      // to return a new access token. So we want to double check we don't have a loggedin cookie.
      if (AppCore.isLoggedIn()) {
        const data: any = {};
        if (!isMainLogicalHost) {
          data.domain = domain;
        }

        response = await AppUtilities.fetchOptionallyAuthorized(
          "LayoutRouting.loadInitialData",
          "/rabbl/api/initialdata",
          "POST",
          data
        );
      } else {
        let url = "/rabbl/api/initialdata";
        if (!isMainLogicalHost) {
          url += "?domain=" + domain;
        }
        response = await AppUtilities.fetchOptionallyAuthorized(
          "LayoutRouting.loadInitialData",
          url,
          "GET",
        );
      }

      if (AppUtilities.DEBUGLEVEL.extra) {
        AppUtilities.dir(response);
      }

      dispatch(setData(response));

    } catch (e) {
      AppUtilities.handleException(e, "LayoutRouting.loadInitialData");
      let errorMessage = AppUtilities.getErrors(e);
      if (AppConstants.IS_DEVELOPMENT) {
        errorMessage += " (" + AppUtilities.getActualHostLink(false, false) + ")";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
      hideSpinner();
    }
  }, [domain, isMainLogicalHost]);

  const checkAge = useCallback(async () => {
    try {
      if (!isAgePassed) {
        // https://docs.expo.dev/versions/latest/sdk/age-range/
        if (AppUtilities.isIOS()) {
          let ageAssuranceRegulationApplies : boolean | null = true;
          try {
            // "Recommended pattern: call this first and only prompt the user for their age
            //  range when the result is not false. When it is false, the user is outside
            //  a regulated jurisdiction and you can skip the age gate entirely."
            ageAssuranceRegulationApplies = await AgeRange.isEligibleForAgeFeaturesAsync();
          } catch (isEligibleException) {
            // "Rejects when the request fails — see AgeRangeService.Error for more information.
            //  Treat rejection as "unknown" and fall through to requestAgeRangeAsync or your own gating logic."

            // Treat this as unknown whether or not we need to check the age range,
            // so assume we do (since an exception is equivalent to not false; see above).
            ageAssuranceRegulationApplies = true;
            AppUtilities.dir(isEligibleException);
          }

          AppUtilities.info("LayoutRouting loadInitialData iOS ageAssuranceRegulationApplies: " + ageAssuranceRegulationApplies);
          if (ageAssuranceRegulationApplies) {

            // "A promise that resolves with user's age range response, or rejects with an error.
            //  The user needs to be signed in on the device to get a valid response. When not
            //  supported (earlier than iOS 26 and web), the call returns lowerBound: 18, which
            //  is equivalent to the response of an adult user."
            const ageRange = await AgeRange.requestAgeRangeAsync({
              threshold1: 18,
            });

            AppUtilities.info("LayoutRouting loadInitialData ageRange.lowerBound = " + ageRange.lowerBound);
            if (ageRange.lowerBound && ageRange.lowerBound >= 18) {
              setNativeAgeFailed(false);
              dispatch(setAgePassed(true));
            } else {
              setNativeAgeFailed(true);
            }
          } else {
            // "When it is false, the user is outside a regulated jurisdiction and you can
            //  skip the age gate entirely."
            setNativeAgeFailed(false);
            dispatch(setAgePassed(true));
          }
        } else if (AppUtilities.isAndroid()) {
          let androidAgeRequest : AgeRange.AgeSignalsStatus | null = null;
          try {
            // "Asks the user to consent to sharing their age signals, showing the
            //  Play Age Signals in-app age sharing consent screen. Play Age Signals
            //  requires this before requestAgeRangeAsync: age signals are only
            //  reported while the status is 'SHARED'."
            androidAgeRequest = await AgeRange.requestAgeSignalsAccessAsync();
          } catch (ageSignalsAccessException) {
            // "Rejects when the request fails."

            // Not clear what this means. They didn't deny sharing the request.
            // Treat this as a success but don't save off to local
            // storage that the age gate passed because the user might update their OS
            // and then we'll require the age check, so we'll check this every time
            androidAgeRequest = null;
            AppUtilities.dir(ageSignalsAccessException);
          }

          AppUtilities.info("LayoutRouting loadInitialData android age signals access: " + androidAgeRequest);
          if (androidAgeRequest) {
            if (androidAgeRequest !== 'SHARED') {
              throw new Error("To use Rabbl on Android, you must consent to sharing your age range.");
            }
            const ageRange = await AgeRange.requestAgeRangeAsync({
              threshold1: 18,
            });

            AppUtilities.info("LayoutRouting loadInitialData ageRange.lowerBound = " + ageRange.lowerBound);
            if (ageRange.lowerBound && ageRange.lowerBound >= 18) {
              setNativeAgeFailed(false);
              dispatch(setAgePassed(true));
            } else {
              setNativeAgeFailed(true);
            }
          } else {
            // Treat this as a success but don't save off to local storage that the age gate passed
            // because the user might update their OS and then we'll require the age check,
            // so we'll check this every time

            // "Resolves with null when Play Age Signals reports no status"

            // Also null for the catch above though not clear what to do with that.
          }

        } else if (AppUtilities.isWeb()) {
          // Nothing to do here because the web modal is handled during render below rather
          // than in this useEffect
        }
      }
    } catch (e) {
      AppUtilities.handleException(e, "LayoutRouting.checkAge");
    }
  }, [isAgePassed]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    checkAge();
  }, [checkAge]);

  if (loading) {
    return null;
  }

  if (error.length > 0) {
    return (
      <SafeAreaView style={AppConstants.STYLES.flex1}>
        <View style={AppConstants.STYLES.container2}>
          <CustomText>Failed to load</CustomText>
          <AppText>{error}</AppText>
          <Button onPress={loadInitialData}>Retry</Button>
        </View>
      </SafeAreaView>
    );
  }

  const hasCommunityMembership =
    (
      communityMembership != null &&
      communityMembership.uniqueid != UNKNOWN_COMMUNITY_MEMBERSHIP.uniqueid
    );

  AppUtilities.info("LayoutRouting render isMainLogicalHost = " + isMainLogicalHost + ", loggedIn: " + loggedIn + ", hasCommunityMembership: " + hasCommunityMembership);

  if (AppUtils.Globals.nextRootRedirect) {
    const nextRedirect = AppUtils.Globals.nextRootRedirect;
    AppUtilities.info("LayoutRouting routing to " + nextRedirect);
    AppUtils.Globals.nextRootRedirect = undefined;
    return <Redirect href={nextRedirect} />
  }

  let shouldShowAgeModalWeb = false;
  if (AppUtilities.isWeb()) {
    if (!isAgePassed) {
      try {
        shouldShowAgeModalWeb = true;

        if (Cookies.get("accessed")) {
          shouldShowAgeModalWeb = false;
        }
      } catch (e) {
        if (AppUtilities.DEBUGLEVEL.extra) {
          AppUtilities.debug("Failed processing cookies: " + e);
        }
      }
    }
  }

  return (
    <CustomKeyboardAvoidingView>
      <Drawer>
        { shouldShowAgeModalWeb && (<AgeModalWeb />) }
        { !AppUtilities.isWeb() && nativeAgeFailed && (<AgeModalNative />) }
        <Stack
          screenOptions={{
            headerTitleStyle: CustomTextStyles.headerTitle,

            // Remove horizontal bottom line for header
            headerShadowVisible: false,

            headerBackTitle: '',

            headerBackButtonDisplayMode: 'minimal',
            
            headerStyle: {
              backgroundColor: 'transparent',
            },

            // TODO maybe too broad? Need to do more testing on iOS
            gestureEnabled: false,
          }}
        >

          <Stack.Protected guard={isMainLogicalHost}>
            <Stack.Screen name="(maindomain-loggedinorout-tabs)" options={{ headerShown: false }} />

            <Stack.Protected guard={!loggedIn}>
              <Stack.Screen name="(maindomain-loggedout-stack)" options={{ headerShown: false }} />
            </Stack.Protected>

            <Stack.Protected guard={loggedIn}>
              <Stack.Screen name="(maindomain-loggedin-stack)" options={{ headerShown: false }} />
            </Stack.Protected>

          </Stack.Protected>

          <Stack.Protected guard={!isMainLogicalHost}>

            <Stack.Protected guard={!loggedIn}>
              <Stack.Screen name="(community-loggedout-stack)" options={{ headerShown: false }} />
            </Stack.Protected>

            <Stack.Protected guard={loggedIn}>

              <Stack.Protected guard={!hasCommunityMembership}>
                <Stack.Screen name="(community-loggedin-nonmember-stack)" options={{ headerShown: false }} />
              </Stack.Protected>

              <Stack.Protected guard={hasCommunityMembership}>
                <Stack.Screen name="(community-loggedin-member-alltabs)" options={{ headerShown: false }} />
                <Stack.Screen name="(community-loggedin-member-stack)" options={{ headerShown: false }} />
                <Stack.Screen name="profile" options={{ headerShown: false }} />
                <Stack.Screen name="chat" options={{ headerShown: false }} />
              </Stack.Protected>

            </Stack.Protected>

            <Stack.Screen name="(community-loggedinorout-stack)" options={{ headerShown: false }} />
            <Stack.Screen name="member" options={{ headerShown: false }} />

          </Stack.Protected>

          <Stack.Screen name="(universal-loggedinorout-stack)" options={{ headerShown: false }} />

          <Stack.Protected guard={loggedIn}>
            <Stack.Screen name="(universal-loggedin-stack)" options={{ headerShown: false }} />
          </Stack.Protected>
          
          <Stack.Protected guard={!loggedIn}>
            <Stack.Screen name="(universal-loggedout-stack)" options={{ headerShown: false }} />
          </Stack.Protected>
          
        </Stack>
      </Drawer>
    </CustomKeyboardAvoidingView>
  );
}
